/*!
 * liveLanding.js — Oscilla Live's first page: start, continue, or join a room
 * © 2026 Rob Canning — GPLv3
 *
 * live.html opens on it (#splash, the start page's look, oscillaSplash.css)
 * unless its address names a session (#s=) or carries code (#code=): then
 * straight to the console, so a session's address, a reload in the middle of
 * a set and a shared link all open where they mean. On the landing:
 *   Start      a new session, the console alone
 *   Example    one of the examples at random, a session of its own, playing
 *   Continue   the sessions kept in this browser, newest first
 *   Join       a new session in a room: its name, its password, yours
 */

import { listSessions, newSessionId } from "./liveSessions.js";
import { useLiveDocs } from "./liveDocs.js";

/** Live's own docs (liveDocs.js useLiveDocs): next to the page, on this server, or oscilla.cc's. */
async function pointDocs(links) {
  const base = await useLiveDocs();
  for (const a of links) a.href = base + a.dataset.doc;
}

/** "14:32", "yesterday", "3 days ago", "12 Oct": when a session was last written in. */
function when(ms) {
  if (!ms) return "";
  const t = new Date(ms), now = new Date();
  const days = (now - t) / 864e5;
  if (days < 1 && now.getDate() === t.getDate()) return t.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  if (days < 2) return "yesterday";
  if (days < 7) return `${Math.floor(days)} days ago`;
  return t.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/** A cell of a list row: a span of class cls holding text. */
const cell = (cls, text) => { const e = document.createElement("span"); e.className = cls; e.textContent = text; return e; };

function sessionItem(s, i, open) {
  const li = document.createElement("li");
  const b = document.createElement("button");
  b.type = "button";
  b.className = "launcher-recent-item";
  b.append(
    cell("n", String(i + 1).padStart(2, "0")),
    cell("t", s.preview || s.id),
    cell("d", s.room ? `room ${s.room}` : ""),
    cell("w", when(s.updatedAt)),
  );
  b.title = `#s=${s.id}`;
  b.onclick = () => open(s.id, s.room);
  li.append(b);
  return li;
}

/**
 * @param {object} o
 * @param {{ join(room, password, name), kept(room), ask(room), room, name }|null} o.rooms  the room box
 * @param {(id: string, room?: string|null) => void} o.open   opens the console on that session
 * @param {{ session?: string, code?: string }} o.asked    what the address names (liveSessions fromAddress)
 * @param {string|null} [o.mediaDir]   Live's own folder on the server's disk (liveMedia.js)
 * @param {() => Promise<void>} [o.example]   an example at random, opened and playing (live.js)
 * @param {(file: string) => Promise<void>} [o.pick]   that example, opened and playing
 * @param {() => Promise<{file, title, about}[]>} [o.listExamples]   the examples, for the list
 */
export function initLanding({ rooms, open, asked, mediaDir = null, example = null, pick = null, listExamples = null }) {
  const page = document.getElementById("splash");
  const hide = () => page?.classList.add("hidden");

  // a session's address (or a shared link, already made into one): the console
  if (!page || asked.session) {
    hide();
    open(asked.session, rooms?.room || null);
    return;
  }

  pointDocs(page.querySelectorAll("a[data-doc]"));
  const media = page.querySelector("#live-media");
  if (media && mediaDir) {
    media.querySelector("code").textContent = mediaDir;
    media.hidden = false;
  }
  // no folder on a server: one from this computer (mediaFolder.js)
  const folder = page.querySelector("#live-folder");
  if (folder && !mediaDir) folder.hidden = false;
  // What is it?, Visualisations: popups
  for (const b of page.querySelectorAll("[data-dialog]")) {
    b.addEventListener("click", () => page.querySelector(`#${b.dataset.dialog}`)?.showModal());
  }
  page.querySelector("#live-start").addEventListener("click", () => { hide(); open(newSessionId(), null); });
  page.querySelector("#live-example")?.addEventListener("click", () => { hide(); example?.(); });
  // the examples by name: one picked, opened fresh and playing
  if (pick && listExamples) {
    listExamples().then(list => {
      const ol = page.querySelector("#live-example-list");
      if (!ol || !list.length) return;
      ol.replaceChildren(...list.map((e, i) => {
        const li = document.createElement("li");
        const b = document.createElement("button");
        b.type = "button";
        b.className = "launcher-recent-item";
        b.append(cell("n", String(i + 1).padStart(2, "0")), cell("t", e.title), cell("d", e.about));
        b.title = e.about;
        b.onclick = () => { hide(); pick(e.file); };
        li.append(b);
        return li;
      }));
      page.querySelector("#live-examples").hidden = false;
    });
  }

  const sessions = listSessions().slice(0, 8);
  const list = page.querySelector("#live-sessions");
  list.replaceChildren(...sessions.map((s, i) => sessionItem(s, i, (id, room) => { hide(); open(id, room); })));
  page.querySelector("#live-continue").hidden = !sessions.length;

  const form = page.querySelector("#live-join");
  // no relay (liveRoomPanel.js relayAddress): no rooms to join
  if (!rooms) { form.closest(".launcher-row-block")?.remove(); (page.querySelector("#live-start")).focus(); return; }
  const field = n => form.elements.namedItem(n);
  const error = page.querySelector("#live-join-error");
  field("room").value = rooms.room ?? "";
  field("name").value = rooms.name ?? "";
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const room = field("room").value.trim();
    const password = field("password").value;
    if (!/^[A-Za-z0-9_-]{1,40}$/.test(room)) { error.textContent = "a room's name: letters, digits, - and _"; return; }
    if (!password) { error.textContent = "a password: the first to join chooses it"; field("password").focus(); return; }
    hide();
    open(newSessionId(), room);
    rooms.join(room, password, field("name").value.trim());
  });
  (field("room").value ? field("password") : page.querySelector("#live-start")).focus();
}
